import React, { useState, useEffect, useRef, MutableRefObject } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { Business, User } from '../types';
import { GOOGLE_MAPS_CONFIG, isGoogleMapsConfigured, GOOGLE_MAPS_ERRORS } from '../config/googleMaps';
import FilteringEngine from '../utils/filteringEngine';
import { MapPin, AlertCircle, Heart } from 'lucide-react';
import { MapBounds, nextZoomOutLevel } from '../utils/mapZoom';

const MAP_PIN_URL = '/map-pin.svg';

const createMapPinElement = (label: string, isHighlyRelevant = false, size = 24): HTMLElement => {
  const wrapper = document.createElement('div');
  wrapper.style.position = 'relative';
  wrapper.style.width = `${size}px`;
  wrapper.style.height = `${Math.round(size * 1.3125)}px`;

  const image = document.createElement('img');
  image.src = MAP_PIN_URL;
  image.alt = label;
  image.width = size;
  image.style.display = 'block';
  image.style.width = `${size}px`;
  image.style.height = 'auto';
  image.style.filter = 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.25))';
  wrapper.appendChild(image);

  if (isHighlyRelevant) {
    const badge = document.createElement('span');
    badge.textContent = '★';
    badge.style.position = 'absolute';
    badge.style.right = '-2px';
    badge.style.top = '-2px';
    badge.style.width = '12px';
    badge.style.height = '12px';
    badge.style.borderRadius = '9999px';
    badge.style.background = '#16a34a';
    badge.style.color = '#ffffff';
    badge.style.fontSize = '8px';
    badge.style.fontWeight = '700';
    badge.style.lineHeight = '12px';
    badge.style.textAlign = 'center';
    badge.style.boxShadow = '0 0 0 2px #ffffff';
    wrapper.appendChild(badge);
  }

  return wrapper;
};

interface GoogleMapViewProps {
  onBusinessSelect: (business: Business) => void;
  onBusinessDeselect?: () => void;
  selectedBusiness: Business | null;
  businesses?: Business[];
  user?: User;

  isDroppingPin?: boolean;
  pendingPinLocation?: { lat: number; lng: number } | null;
  onPinDrop?: (coords: { lat: number; lng: number }) => void;
  onCancelPinDrop?: () => void;

  showPinConfirmPrompt?: boolean;
  onConfirmPin?: () => void;

  onMapViewChange?: (
    center: { lat: number; lng: number },
    zoom: number,
    bounds?: MapBounds,
  ) => void;
  onStartReview?: (business: Business) => void;
  onLeaveReviewClick?: () => void;
  /** Optional ref the parent can call to zoom the map out one step. */
  zoomOutRef?: MutableRefObject<(() => void) | null>;
}

interface MapProps {
  businesses: Business[];
  selectedBusiness: Business | null;
  onBusinessSelect: (business: Business) => void;
  onBusinessDeselect?: () => void;
  user?: User;

  isDroppingPin?: boolean;
  pendingPinLocation?: { lat: number; lng: number } | null;
  onPinDrop?: (coords: { lat: number; lng: number }) => void;
  onCancelPinDrop?: () => void;

  showPinConfirmPrompt?: boolean;
  onConfirmPin?: () => void;

  onMapViewChange?: (
    center: { lat: number; lng: number },
    zoom: number,
    bounds?: MapBounds,
  ) => void;
  onStartReview?: (business: Business) => void;
  zoomOutRef?: MutableRefObject<(() => void) | null>;
}

type BusinessMarker = google.maps.marker.AdvancedMarkerElement & {
  businessId?: string;
};

// Main Map Component
const Map: React.FC<MapProps> = ({ businesses, selectedBusiness, onBusinessSelect, onBusinessDeselect, user,
  isDroppingPin = false,
  pendingPinLocation = null,
  onPinDrop,
  onMapViewChange,
  onStartReview,
  zoomOutRef,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [markers, setMarkers] = useState<BusinessMarker[]>([]);
  const [infoWindow, setInfoWindow] = useState<google.maps.InfoWindow>();
  // Wait for geolocation (or fallback) before the first viewport fetch so we
  // don't briefly load default-center businesses then wipe them.
  const [initialLocationReady, setInitialLocationReady] = useState(false);


  const mapRef = useRef<google.maps.Map | null>(null);
  const pinMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const lastAutoFocusedBusinessIdRef = useRef<string | null>(null);


  // Initialize map
  useEffect(() => {
    if (ref.current && !map) {
      const controlOptions = {
        disableDefaultUI: true,
        zoomControl: false,
        streetViewControl: false,
        cameraControl: false,
        rotateControl: false,
        mapTypeControl: false,
        scaleControl: false,
        fullscreenControl: true,
      } as const;

      const newMap = new google.maps.Map(ref.current, {
        center: GOOGLE_MAPS_CONFIG.defaultCenter,
        zoom: GOOGLE_MAPS_CONFIG.defaultZoom,
        mapId: GOOGLE_MAPS_CONFIG.mapId,
        ...GOOGLE_MAPS_CONFIG.mapOptions,
        ...controlOptions,
      });

      // Re-assert after init — some Google Maps builds briefly restore default chrome.
      newMap.setOptions(controlOptions);

      mapRef.current = newMap;

      const newInfoWindow = new google.maps.InfoWindow();

      setMap(newMap);
      setInfoWindow(newInfoWindow);
    }
  }, [ref, map]);


  // Center map to user's location on load
  useEffect(() => {
    if (mapRef.current) {
      centerToMyLocation();
    }
  }, [mapRef.current]);

  // Expose a one-step zoom-out action for the empty-area CTA
  useEffect(() => {
    if (!zoomOutRef) return;

    zoomOutRef.current = () => {
      if (!mapRef.current) return;
      const currentZoom = mapRef.current.getZoom() ?? GOOGLE_MAPS_CONFIG.defaultZoom;
      mapRef.current.setZoom(nextZoomOutLevel(currentZoom));
    };

    return () => {
      zoomOutRef.current = null;
    };
  }, [zoomOutRef, map]);


  // Handle map clicks for dropping pin
  useEffect(() => {
    if (!mapRef.current) return;

    const listener = mapRef.current.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (!isDroppingPin || !event.latLng) return;

      const placeId = (event as any).placeId;
      // console.log("placeId: ", placeId);

      const coords = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      // console.log(`Latitude: ${coords.lat.toFixed(6)}\nLongitude: ${coords.lng.toFixed(6)}`);
      onPinDrop?.(coords);
    });

    return () => listener.remove();
  }, [isDroppingPin, onPinDrop]);


  // Update pin marker position
  useEffect(() => {
    if (!pendingPinLocation || !mapRef.current) {

      if (pinMarkerRef.current) {
        pinMarkerRef.current.map = null;
        pinMarkerRef.current = null;
      }
      return;
    }

    if (!pinMarkerRef.current) {
      pinMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
        map: mapRef.current,
        position: pendingPinLocation,
        content: createMapPinElement('Pending business location', false, 26),
        gmpClickable: false,
      });
    } else {
      pinMarkerRef.current.position = pendingPinLocation;
    }

    mapRef.current.panTo(pendingPinLocation);
  }, [pendingPinLocation]);


  // change cursor when dropping pin
  useEffect(() => {
    if (!map) return;
    map.setOptions({
      draggableCursor: isDroppingPin ? `url(${MAP_PIN_URL}) 24 63, crosshair` : undefined,
    });
  }, [map, isDroppingPin]);


  // Debounce helper function (place ABOVE the useEffect)
  const debounce = <T extends (...args: any[]) => void>(func: T, wait: number) => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    return (...args: Parameters<T>) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // handleMapViewChange — only after initial geolocation settles
  const lastViewRef = useRef<{
    lat: number;
    lng: number;
    zoom: number;
    north: number;
    south: number;
    east: number;
    west: number;
  } | null>(null);
  useEffect(() => {
    if (!mapRef.current || !onMapViewChange || !initialLocationReady) return;

    const handleMapViewChange = () => {
      const center = mapRef.current!.getCenter();
      const zoom = mapRef.current!.getZoom();
      const latLngBounds = mapRef.current!.getBounds();
      if (!center || zoom == null || !latLngBounds) return;

      // round for change-detection only (keep number)
      const lat = Number(center.lat().toFixed(3));
      const lng = Number(center.lng().toFixed(3));
      const north = Number(latLngBounds.getNorthEast().lat().toFixed(3));
      const south = Number(latLngBounds.getSouthWest().lat().toFixed(3));
      const east = Number(latLngBounds.getNorthEast().lng().toFixed(3));
      const west = Number(latLngBounds.getSouthWest().lng().toFixed(3));

      // ✅ prevent calling if nothing changed
      const prev = lastViewRef.current;
      if (
        prev &&
        prev.lat === lat &&
        prev.lng === lng &&
        prev.zoom === zoom &&
        prev.north === north &&
        prev.south === south &&
        prev.east === east &&
        prev.west === west
      ) {
        return;
      }

      lastViewRef.current = { lat, lng, zoom, north, south, east, west };

      onMapViewChange(
        { lat: center.lat(), lng: center.lng() },
        zoom,
        {
          north: latLngBounds.getNorthEast().lat(),
          south: latLngBounds.getSouthWest().lat(),
          east: latLngBounds.getNorthEast().lng(),
          west: latLngBounds.getSouthWest().lng(),
        },
      );
    };

    const debouncedHandler = debounce(handleMapViewChange, 400);

    // ONLY listen on idle
    const idleListener = mapRef.current.addListener('idle', debouncedHandler);

    // initial fetch for the settled location
    const initialTimeout = setTimeout(handleMapViewChange, 50);

    return () => {
      idleListener.remove();
      clearTimeout(initialTimeout);
    };
  }, [onMapViewChange, initialLocationReady]);

  // Function to center map to user's current location
  const centerToMyLocation = () => {
    const fallback = GOOGLE_MAPS_CONFIG.defaultCenter;
    if (!mapRef.current) return;

    const finish = () => setInitialLocationReady(true);

    if (!navigator?.geolocation) {
      mapRef.current.setCenter(fallback);
      mapRef.current.setZoom(GOOGLE_MAPS_CONFIG.defaultZoom);
      finish();
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        mapRef.current!.setCenter(coords);
        mapRef.current!.setZoom(GOOGLE_MAPS_CONFIG.defaultZoom);

        // Drop a marker at the user's current location
        const marker = new google.maps.Marker({
          position: coords,
          map: mapRef.current!,
          title: 'Current location' + coords.lat.toFixed(4) + ', ' + coords.lng.toFixed(4),
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#2563eb',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2
          }
        });

        marker.addListener('click', () => {
          mapRef.current?.setZoom(15);
          mapRef.current?.setCenter(coords);
        });
        finish();
      },
      () => {
        mapRef.current!.setCenter(fallback);
        mapRef.current!.setZoom(GOOGLE_MAPS_CONFIG.defaultZoom);
        finish();
      },
      { enableHighAccuracy: true, timeout: 5000 }
    );
  };




  // Businesses are already pre-filtered by App.tsx, use them directly for map markers
  const filteredBusinesses = businesses;

  // Create marker content
  const createMarkerContent = (business: Business) => {
    const relevanceScore = user && user.interests ?
      FilteringEngine.calculateBusinessRelevance(business, user.interests) : 0;
    const isHighlyRelevant = relevanceScore >= 7;

    const stars = Array.from({ length: 5 }, (_, i) =>
      i < Math.floor(business.averageRating || 0) ? '★' : '☆'
    ).join('');

    return `
      <div style="max-width: 300px; padding: 12px;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px;">
          <h3 style="font-size: 16px; font-weight: 600; margin: 0; color: #1f2937;">${business.name}</h3>
          <div style="display: flex; align-items: center; margin-left: 8px;">
            <span style="color: #fbbf24; font-size: 14px;">${stars}</span>
            <span style="font-size: 12px; color: #6b7280; margin-left: 4px;">(${business.totalReviews || 0})</span>
          </div>
        </div>
        
        <p style="font-size: 12px; color: #6b7280; margin: 0 0 8px 0;">${business.category}</p>
        
        <div style="display: flex; align-items: flex-start; margin-bottom: 12px;">
          <span style="color: #9ca3af; margin-right: 4px;">📍</span>
          <span style="font-size: 12px; color: #6b7280;">${business.address}</span>
        </div>

        ${business.description ? `
          <p style="font-size: 12px; color: #374151; margin: 0 0 12px 0; line-height: 1.4;">
            ${business.description.length > 100 ? business.description.substring(0, 100) + '...' : business.description}
          </p>
        ` : ''}
        
        <div style="display: flex; gap: 8px; margin-bottom: 8px;">
          <button 
            onclick="window.showSomeLove('${business.id}')"
            style="
              flex: 1;
              background: #db2777; 
              color: white; 
              border: none; 
              padding: 8px 12px; 
              border-radius: 6px; 
              font-size: 12px; 
              font-weight: 600; 
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 4px;
            "
            onmouseover="this.style.background='#be185d'"
            onmouseout="this.style.background='#db2777'"
          >
            ❤️ Show Some Love
          </button>
          <button 
            onclick="window.selectBusiness('${business.id}')"
            style="
              background: #f3f4f6; 
              color: #374151; 
              border: none; 
              padding: 8px 12px; 
              border-radius: 6px; 
              font-size: 12px; 
              font-weight: 500; 
              cursor: pointer;
            "
            onmouseover="this.style.background='#e5e7eb'"
            onmouseout="this.style.background='#f3f4f6'"
          >
            Details
          </button>
        </div>
          
        ${isHighlyRelevant ? `
          <div style="
            display: flex; 
            align-items: center; 
            background: #fef3c7; 
            color: #d97706; 
            padding: 4px 8px; 
            border-radius: 6px; 
            font-size: 12px;
            margin-bottom: 8px;
          ">
            <span style="margin-right: 4px;">✨</span>
            <span>Matches your interests</span>
          </div>
        ` : ''}
        
        ${business.phone || business.website ? `
          <div style="
            display: flex; 
            justify-content: space-between; 
            margin-top: 8px; 
            padding-top: 8px; 
            border-top: 1px solid #e5e7eb;
          ">
            <div style="display: flex; gap: 12px;">
              ${business.phone ? `
                <a href="tel:${business.phone}" 
                   style="font-size: 12px; color: #2563eb; text-decoration: none;">
                  Call
                </a>
              ` : ''}
              ${business.website ? `
                <a href="${business.website}" target="_blank" 
                   style="font-size: 12px; color: #2563eb; text-decoration: none;">
                  Website
                </a>
              ` : ''}
            </div>
            
            ${business.priceRange ? `
              <span style="font-size: 12px; color: #6b7280; font-weight: 500;">
                ${business.priceRange}
              </span>
            ` : ''}
          </div>
        ` : ''}
      </div>
    `;
  };

  // Global function to handle business selection from info window
  useEffect(() => {
    (window as any).selectBusiness = (businessId: string) => {
      const business = businesses.find(b => b.id === businessId);
      if (business) {
        onBusinessSelect(business);
        infoWindow?.close();
      }
    };

    (window as any).showSomeLove = (businessId: string) => {
      const business = businesses.find(b => b.id === businessId);
      if (business) {
        onBusinessSelect(business);
        infoWindow?.close();
        onStartReview?.(business);
      }
    };

    return () => {
      delete (window as any).selectBusiness;
      delete (window as any).showSomeLove;
    };
  }, [businesses, onBusinessSelect, infoWindow, onStartReview]);

  useEffect(() => {
    if (!infoWindow) return;

    const listener = infoWindow.addListener('closeclick', () => {
      lastAutoFocusedBusinessIdRef.current = null;
      onBusinessDeselect?.();
    });

    return () => listener.remove();
  }, [infoWindow, onBusinessDeselect]);

  // Update markers when businesses change
  useEffect(() => {
    if (!map || !infoWindow) return;

    // Clear existing markers
    markers.forEach(marker => {
      marker.map = null;
    });

    // Create new markers
    const newMarkers = filteredBusinesses.map(business => {
      const relevanceScore = user && user.interests ?
        FilteringEngine.calculateBusinessRelevance(business, user.interests) : 0;
      const isHighlyRelevant = relevanceScore >= 7;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: business.latitude, lng: business.longitude },
        map,
        title: business.name,
        content: createMapPinElement(business.name, isHighlyRelevant)
      }) as BusinessMarker;
      marker.businessId = business.id;

      // marker.append(pin);

      marker.addListener('click', () => {
        onBusinessSelect(business); // This will populate the sidebar
        infoWindow.setContent(createMarkerContent(business));
        infoWindow.open(map, marker);
      });

      return marker;
    });

    setMarkers(newMarkers);
  }, [map, filteredBusinesses, user, infoWindow, onBusinessSelect]);

  // Handle selected business
  useEffect(() => {
    if (!selectedBusiness) {
      lastAutoFocusedBusinessIdRef.current = null;
      infoWindow?.close();
      return;
    }

    if (map && infoWindow) {
      if (lastAutoFocusedBusinessIdRef.current === selectedBusiness.id) return;

      const marker = markers.find(m =>
        m.businessId === selectedBusiness.id
      );

      if (marker) {
        lastAutoFocusedBusinessIdRef.current = selectedBusiness.id;
        map.panTo(marker.position!);
        map.setZoom(15);
        infoWindow.setContent(createMarkerContent(selectedBusiness));
        infoWindow.open(map, marker);
      }
    }
  }, [selectedBusiness, map, markers, infoWindow]);

  return <div ref={ref} style={{ width: '100%', height: '100%' }} />;
};

// Loading component
const MapLoadingComponent: React.FC = () => (
  <div className="flex items-center justify-center h-full bg-gray-100">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading Google Maps...</p>
    </div>
  </div>
);

// Error component
const MapErrorComponent: React.FC<{ error: string }> = ({ error }) => {
  const isApiKeyError = error.includes('ApiTargetBlockedMapError') || error.includes('LOAD_FAILED');

  return (
    <div className="flex items-center justify-center h-full bg-red-50">
      <div className="text-center p-6 max-w-2xl">
        <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-900 mb-2">Google Maps Error</h3>
        <p className="text-red-700 mb-4">{error}</p>

        {isApiKeyError ? (
          <div className="text-sm text-red-600 bg-red-100 rounded-lg p-4 space-y-3">
            <p className="font-medium">🔑 API Key Issue Detected</p>

            <div className="text-left">
              <p className="font-medium mb-2">Quick Fix Options:</p>

              <div className="bg-white rounded p-3 mb-3">
                <p className="font-medium text-red-800 mb-1">Option 1: Remove Restrictions (Fastest)</p>
                <ol className="space-y-1 text-xs">
                  <li>1. Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" className="text-blue-600 underline">Google Cloud Console</a></li>
                  <li>2. Click on your API key</li>
                  <li>3. Set Application restrictions to "None"</li>
                  <li>4. Save and refresh this page</li>
                </ol>
              </div>

              <div className="bg-white rounded p-3">
                <p className="font-medium text-red-800 mb-1">Option 2: Add Localhost (More Secure)</p>
                <ol className="space-y-1 text-xs">
                  <li>1. In API key settings, choose "HTTP referrers"</li>
                  <li>2. Add: <code className="bg-gray-200 px-1">localhost:*</code></li>
                  <li>3. Add: <code className="bg-gray-200 px-1">127.0.0.1:*</code></li>
                  <li>4. Save and refresh this page</li>
                </ol>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-red-600 bg-red-100 rounded-lg p-3">
            <p className="font-medium mb-2">Setup Steps:</p>
            <ol className="text-left space-y-1">
              <li>1. Create a <code>.env</code> file in your project root</li>
              <li>2. Add: <code>REACT_APP_GOOGLE_MAPS_API_KEY=your_api_key_here</code></li>
              <li>3. Replace with your actual Google Maps API key</li>
              <li>4. Restart the development server</li>
            </ol>
          </div>
        )}

        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry After Fixing
        </button>
      </div>
    </div>
  );
};

// Render function for different loading states
const render = (status: Status, error?: Error): React.ReactElement => {
  if (status === Status.LOADING) return <MapLoadingComponent />;
  if (status === Status.FAILURE) {
    const errorMessage = error?.message || GOOGLE_MAPS_ERRORS.LOAD_FAILED;
    return <MapErrorComponent error={errorMessage} />;
  }
  return <div />;
};

// Main component
const GoogleMapView: React.FC<GoogleMapViewProps> = ({
  onBusinessSelect,
  onBusinessDeselect,
  selectedBusiness,
  businesses = [],
  user,

  isDroppingPin = false, //pat
  pendingPinLocation = null,
  onPinDrop,
  onCancelPinDrop,

  showPinConfirmPrompt = false,
  onConfirmPin,

  onMapViewChange,
  onStartReview,
  onLeaveReviewClick,
  zoomOutRef,
}) => {
  // Check if Google Maps is configured
  if (!isGoogleMapsConfigured()) {
    return <MapErrorComponent error={GOOGLE_MAPS_ERRORS.NO_API_KEY} />;
  }

  return (
    <div className="h-full w-full relative">
      <Wrapper
        apiKey={GOOGLE_MAPS_CONFIG.apiKey}
        render={render}
        libraries={['places', 'marker']}
      >
        <Map
          businesses={businesses}
          selectedBusiness={selectedBusiness}
          onBusinessSelect={onBusinessSelect}
          onBusinessDeselect={onBusinessDeselect}
          user={user}

          isDroppingPin={isDroppingPin}
          pendingPinLocation={pendingPinLocation}
          onPinDrop={onPinDrop}
          onCancelPinDrop={onCancelPinDrop}

          onMapViewChange={onMapViewChange}
          onStartReview={onStartReview}
          zoomOutRef={zoomOutRef}
        />
      </Wrapper>

      {/* Map Controls */}
      <div className="absolute top-4 left-4 z-10">
        <div className="bg-white rounded-lg shadow-md p-3">
          <div className="flex items-center space-x-2 text-sm text-gray-700">
            <MapPin className="w-4 h-4 text-primary-600" />
            <span className="font-medium">Discover Businesses</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Click on markers to see details
          </p>
        </div>
      </div>

      {showPinConfirmPrompt && pendingPinLocation && (
        <div className="absolute inset-0 flex items-end justify-center pb-6 z-20 pointer-events-none">
          <div className="bg-pink-600 rounded-lg shadow px-4 py-3 flex flex-col sm:flex-row items-center justify-center gap-3 pointer-events-auto">
            <span className="text-sm font-medium text-white text-center">
              Tap anywhere to adjust the pin, then confirm.
            </span>
            <div className="flex gap-2 justify-center">
              <button
                className="px-4 py-2 bg-white text-pink-600 rounded font-medium"
                onClick={onConfirmPin}
              >
                Use this location
              </button>
              <button
                className="px-4 py-2 bg-gray-100 text-gray-800 rounded"
                onClick={onCancelPinDrop}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Legend */}
      {(!selectedBusiness && !showPinConfirmPrompt) && (
        <div className="absolute bottom-4 left-4 z-10">
          <div className="bg-white rounded-lg shadow-md p-3">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <img src={MAP_PIN_URL} alt="" className="h-5 w-auto" />
                <span className="text-xs text-gray-700">Business Location</span>
              </div>
              {user && user.interests && user.interests.length > 0 && (
                <div className="flex items-center space-x-2">
                  <div className="relative h-5 w-4">
                    <img src={MAP_PIN_URL} alt="" className="h-5 w-auto" />
                    <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-green-600 text-[8px] font-bold leading-3 text-white shadow-[0_0_0_2px_#fff]">
                      ★
                    </span>
                  </div>
                  <span className="text-xs text-gray-700">High Match</span>
                </div>
              )}
            </div>
          </div>
        </div>)}

      {/* Show Some Love / Leave a Review overlay */}
      {onLeaveReviewClick && !showPinConfirmPrompt && !isDroppingPin && (
        <div className="absolute bottom-4 right-4 z-10">
          <button
            type="button"
            onClick={onLeaveReviewClick}
            className="flex items-center gap-2 bg-accent-600 hover:bg-accent-700 text-white font-semibold px-4 py-3 rounded-lg shadow-lg transition-colors duration-200"
            aria-label="Show some love — leave a review"
          >
            <Heart className="w-5 h-5 fill-current" />
            <span className="text-sm sm:text-base">
              {selectedBusiness ? 'Show Some Love' : 'Leave a Review'}
            </span>
          </button>
        </div>
      )}

    </div>
  );
};

export default GoogleMapView;
